/**
 * 认证上下文
 * 管理用户登录状态和认证操作
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { AuthApiService, User, AuthResponse } from '../services/api';

// 认证上下文类型
interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;

    // OAuth 登录
    loginWithGoogle: () => Promise<void>;
    loginWithMicrosoft: () => Promise<void>;
    handleOAuthCallback: (provider: string, code: string) => Promise<boolean>;

    // Magic Link 登录
    sendMagicLink: (email: string, language?: string) => Promise<{ success: boolean; error?: string }>;
    verifyMagicLink: (email: string, token: string) => Promise<boolean>;

    // SMS 登录 (中国)
    sendSMSOtp: (phone: string) => Promise<{ success: boolean; error?: string }>;
    verifySMSOtp: (phone: string, code: string) => Promise<boolean>;

    // 游客登录 (仅测试用)
    loginAsGuest: () => void;

    // 刷新用户信息 (强制从后端获取)
    refreshUser: () => Promise<void>;

    // 登出
    logout: () => Promise<void>;

    // 清除错误
    clearError: () => void;
}

// 创建上下文
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 检测是否在中国 (通过语言/时区)
export function detectChinaRegion(): boolean {
    const language = navigator.language.toLowerCase();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return (
        language.startsWith('zh-cn') ||
        language === 'zh' ||
        timezone === 'Asia/Shanghai' ||
        timezone === 'Asia/Chongqing'
    );
}

// 认证提供者组件
export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const authProcessed = useRef(false);

    // 初始化 - 检查现有会话或处理 OAuth 回调
    useEffect(() => {
        // 检查是否为 OAuth 回调 URL
        const handleOAuthRedirect = async () => {
            const pathname = window.location.pathname;
            const searchParams = new URLSearchParams(window.location.search);
            const code = searchParams.get('code');
            const state = searchParams.get('state');

            // 检查是否是 OAuth 回调路径 (/auth/callback/:provider)
            const callbackMatch = pathname.match(/^\/auth\/callback\/(google|microsoft|wechat)$/);

            if (callbackMatch && code) {
                // Prevent double execution in Strict Mode
                if (authProcessed.current) {
                    console.log('OAuth callback already processing/processed, skipping');
                    return true;
                }
                authProcessed.current = true;

                const provider = callbackMatch[1];
                const savedState = sessionStorage.getItem('oauth_state');

                // 验证 state 以防止 CSRF 攻击
                if (state && savedState && state === savedState) {
                    console.log(`Processing OAuth callback for ${provider}`);
                    setIsLoading(true);

                    try {
                        const response = await AuthApiService.handleOAuthCallback(provider, code);

                        if (response.success && response.user && response.session) {
                            localStorage.setItem('auth_token', response.session.token);
                            localStorage.setItem('user', JSON.stringify(response.user));
                            setUser(response.user);
                            setError(null);
                            console.log('OAuth login successful:', response.user.email || response.user.name);
                        } else {
                            setError(response.error || 'Authentication failed');
                            console.error('OAuth callback failed:', response.error);
                        }
                    } catch (err) {
                        console.error('OAuth callback error:', err);
                        setError('Login failed. Please try again.');
                    } finally {
                        // 清理 sessionStorage 和 URL
                        sessionStorage.removeItem('oauth_state');
                        sessionStorage.removeItem('oauth_provider');

                        // 清除 URL 中的 OAuth 参数，重定向到根路径
                        window.history.replaceState({}, document.title, '/');
                        setIsLoading(false);
                    }
                } else {
                    console.warn('OAuth state mismatch or missing');
                    // 清除 URL 中的 OAuth 参数
                    window.history.replaceState({}, document.title, '/');
                }
                return true; // 表示处理了 OAuth 回调
            }

            // 检查是否是 Magic Link 回调路径 (/auth/verify?token=...&email=...)
            if (pathname === '/auth/verify') {
                const token = searchParams.get('token');
                const email = searchParams.get('email');

                if (token && email) {
                    // Prevent double execution in Strict Mode
                    if (authProcessed.current) {
                        console.log('Magic Link callback already processing/processed, skipping');
                        return true;
                    }
                    authProcessed.current = true;

                    console.log('Processing Magic Link callback for:', email);
                    setIsLoading(true);

                    try {
                        const response = await AuthApiService.verifyMagicLink(email, token);

                        if (response.success && response.user && response.session) {
                            localStorage.setItem('auth_token', response.session.token);
                            localStorage.setItem('user', JSON.stringify(response.user));
                            setUser(response.user);
                            setError(null);
                            console.log('Magic Link login successful:', response.user.email || response.user.name);
                        } else {
                            setError(response.error || 'Invalid or expired link');
                            console.error('Magic Link callback failed:', response.error);
                        }
                    } catch (err) {
                        console.error('Magic Link callback error:', err);
                        setError('Login failed. Please try again.');
                    } finally {
                        // 清除 URL 中的参数，重定向到根路径
                        window.history.replaceState({}, document.title, '/');
                        setIsLoading(false);
                    }
                    return true; // 表示处理了 Magic Link 回调
                }
            }

            return false; // 不是任何回调
        };

        // 首先尝试处理 OAuth 回调，如果不是回调则检查会话
        handleOAuthRedirect().then((wasOAuthCallback) => {
            if (!wasOAuthCallback) {
                checkSession();
            }
        });
    }, []);

    // 检查会话 - 始终从后端获取最新用户数据
    const checkSession = async () => {
        setIsLoading(true);
        try {
            const savedToken = localStorage.getItem('auth_token');

            // 如果没有 token，用户未登录
            if (!savedToken) {
                setIsLoading(false);
                return;
            }

            // 始终从后端 API 获取最新用户数据（包括会员状态）
            const result = await AuthApiService.getSession();
            if (result.authenticated && result.user) {
                // 更新 localStorage 中的 user 数据
                localStorage.setItem('user', JSON.stringify(result.user));
                setUser(result.user);
                console.log('从后端恢复登录状态:', result.user.email || result.user.name, '会员:', result.user.tier || 'free');
            } else {
                // token 无效，清除本地数据
                console.warn('Token 无效或已过期，清除登录状态');
                localStorage.removeItem('auth_token');
                localStorage.removeItem('user');
                setUser(null);
            }
        } catch (err) {
            console.error('Session check failed:', err);
            // 网络错误时，尝试使用 localStorage 缓存作为回退
            const savedUser = localStorage.getItem('user');
            if (savedUser) {
                try {
                    const parsedUser = JSON.parse(savedUser);
                    if (parsedUser && parsedUser.id) {
                        setUser(parsedUser);
                        console.log('网络错误，使用 localStorage 缓存:', parsedUser.email);
                    }
                } catch {
                    // ignore parse error
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 刷新用户信息 (强制从后端获取)
    const refreshUser = async () => {
        try {
            const result = await AuthApiService.getSession();
            if (result.authenticated && result.user) {
                // 同步更新 localStorage 中的 user 数据
                localStorage.setItem('user', JSON.stringify(result.user));
                setUser(result.user);
                console.log('User profile refreshed:', result.user.email);
            }
        } catch (err) {
            console.error('Failed to refresh user profile:', err);
        }
    };

    // 处理认证响应
    const handleAuthResponse = useCallback((response: AuthResponse): boolean => {
        if (response.success && response.user && response.session) {
            localStorage.setItem('auth_token', response.session.token);
            setUser(response.user);
            setError(null);
            return true;
        } else {
            setError(response.error || 'Authentication failed');
            return false;
        }
    }, []);

    // OAuth 登录通用方法
    const initiateOAuth = async (provider: 'google' | 'microsoft' | 'wechat') => {
        setIsLoading(true);
        setError(null);
        try {
            const { url, state } = await AuthApiService.getOAuthUrl(provider);
            // 保存 state 用于 CSRF 验证
            sessionStorage.setItem('oauth_state', state);
            sessionStorage.setItem('oauth_provider', provider);
            // 重定向到 OAuth 提供商
            window.location.href = url;
        } catch (err) {
            setError('Failed to initiate login');
            setIsLoading(false);
        }
    };

    // Google 登录
    const loginWithGoogle = async () => {
        await initiateOAuth('google');
    };

    // Microsoft 登录
    const loginWithMicrosoft = async () => {
        await initiateOAuth('microsoft');
    };

    // 处理 OAuth 回调
    const handleOAuthCallback = async (provider: string, code: string): Promise<boolean> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await AuthApiService.handleOAuthCallback(provider, code);
            return handleAuthResponse(response);
        } catch (err) {
            setError('Login failed. Please try again.');
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // 发送 Magic Link
    const sendMagicLink = async (email: string, language?: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await AuthApiService.sendMagicLink(email, language);
            if (!result.success) {
                setError(result.error || 'Failed to send magic link');
            }
            return result;
        } catch (err) {
            const errorMsg = 'Failed to send magic link';
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } finally {
            setIsLoading(false);
        }
    };

    // 验证 Magic Link
    const verifyMagicLink = async (email: string, token: string): Promise<boolean> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await AuthApiService.verifyMagicLink(email, token);
            return handleAuthResponse(response);
        } catch (err) {
            setError('Invalid or expired link');
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // 发送 SMS 验证码
    const sendSMSOtp = async (phone: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await AuthApiService.sendSMSOtp(phone);
            if (!result.success) {
                setError(result.error || 'Failed to send verification code');
            }
            return result;
        } catch (err) {
            const errorMsg = 'Failed to send verification code';
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } finally {
            setIsLoading(false);
        }
    };

    // 验证 SMS 验证码
    const verifySMSOtp = async (phone: string, code: string): Promise<boolean> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await AuthApiService.verifySMSOtp(phone, code);
            return handleAuthResponse(response);
        } catch (err) {
            setError('Invalid verification code');
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    // 登出
    const logout = async () => {
        setIsLoading(true);
        try {
            await AuthApiService.logout();
        } finally {
            setUser(null);
            setIsLoading(false);
        }
    };

    // 游客登录 (仅测试用)
    const loginAsGuest = () => {
        // 尝试恢复已有的 guest ID，避免每次都生成新的
        let guestId = localStorage.getItem('guest_user_id');
        if (!guestId) {
            guestId = 'guest-' + Date.now();
            localStorage.setItem('guest_user_id', guestId);
        }

        const guestUser: User = {
            id: guestId,
            name: 'Test User',
            email: 'test@example.com',
        };
        setUser(guestUser);
        setError(null);
        console.log('已以游客身份登录（仅供测试）, ID:', guestId);
    };

    // 清除错误
    const clearError = () => setError(null);

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        loginWithGoogle,
        loginWithMicrosoft,
        handleOAuthCallback,
        sendMagicLink,
        verifyMagicLink,
        sendSMSOtp,
        verifySMSOtp,
        loginAsGuest,
        logout,
        clearError,
        refreshUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// 自定义 Hook
export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
